import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";

const CATEGORIES = ["Invoices", "Receipts", "Medical Records", "Building Plans", "Supplier Documents", "Guarantees", "Photos", "Contracts", "Reports"];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  async function refresh() {
    try { const { data } = await api.get("/documents"); setDocs(data); }
    catch { toast.error("Could not load Documents"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <PageHero
        eyebrow="Documents"
        title="Central document repository"
        subtitle="Invoices, receipts, medical records, contracts and photos."
        moduleKind="reports"
      />

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>All documents</CardTitle><CardDescription>{docs.length} on file</CardDescription></div>
          <NewDoc onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Tags</TableHead>
              <TableHead>Uploaded</TableHead><TableHead className="text-right">File</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" />{d.title}</TableCell>
                  <TableCell><Badge variant="outline">{d.category}</Badge></TableCell>
                  <TableCell className="text-xs">{(d.tags || []).join(", ")}</TableCell>
                  <TableCell>{fmtDate(d.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {d.file_id ? (
                      <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-deepgreen hover:underline">
                        <Download className="h-3 w-3" /> Open
                      </a>
                    ) : <span className="subtle text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center subtle py-8">No documents on file yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewDoc({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Invoices", tags: "", description: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function submit() {
    if (!form.title) { toast.error("Title required"); return; }
    setUploading(true);
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = data.id;
      }
      await api.post("/documents", {
        title: form.title, category: form.category,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        description: form.description, file_id,
      });
      toast.success("Document added"); setOpen(false); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-doc-btn"><Plus className="h-4 w-4 mr-1" />Upload document</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New document</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title-input" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="col-span-2">
            <Label>File (optional — up to 20MB)</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
          </div>
        </div>
        <DialogFooter><Button disabled={uploading} onClick={submit} data-testid="doc-save-btn">{uploading ? "Uploading…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
